import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  useGetOrderByIdQuery,
  useUploadOrderReceiptMutation,
  useUpdateOrderStatusMutation,
} from "../store/api";

const Container = styled.div`
  background: var(--gradient-bg);
  color: var(--text-primary);
  min-height: 100vh;
  padding: 1rem;
  font-family: Arial, sans-serif;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    color: var(--text-primary);
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  max-width: 900px;
`;

const LeftPanel = styled.div`
  background: var(--bg-glass);
  backdrop-filter: blur(12px);
  border: 1px solid var(--border-light);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: var(--shadow-card);
`;

const RightPanel = styled.div`
  background: var(--bg-glass);
  backdrop-filter: blur(12px);
  border: 1px solid var(--border-light);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: var(--shadow-card);
`;

const DealHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const DealTitle = styled.h1`
  font-size: 2rem;
  font-weight: normal;
  margin: 0;
`;

const DealId = styled.span`
  background: var(--accent-primary-light);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-family: monospace;
  color: var(--accent-primary);
  border: 1px solid var(--accent-primary-border);
`;

const StatusSection = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-bottom: 2rem;
`;

const StatusItem = styled.div`
  text-align: center;
`;

const StatusLabel = styled.div`
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
`;

const StatusValue = styled.div`
  font-size: 1.1rem;
  font-weight: 500;
`;

const StatusBadge = styled.span<{ status: string }>`
  background: ${(props) => {
    switch (props.status) {
      case "completed":
        return "var(--accent-neutral)"; /* TODO: дизайнерский серый из --gradient-withdraw — нет точного var */
      case "pending":
        return "var(--accent-primary)";
      case "processing":
        return "var(--accent-primary)";
      case "failed":
        return "var(--accent-error)";
      default:
        return "var(--text-secondary)";
    }
  }};
  color: #ffffff;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
`;

const AmountSection = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const AmountItem = styled.div`
  text-align: center;
`;

const AmountLabel = styled.div`
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
  font-weight: 600;
`;

const AmountDescription = styled.div`
  color: var(--text-muted);
  font-size: 0.75rem;
  margin-bottom: 0.5rem;
  font-style: italic;
`;

const AmountValue = styled.div<{ positive?: boolean; negative?: boolean }>`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${(props) =>
    /* TODO: positive "var(--accent-neutral)" — дизайнерский серый из --gradient-withdraw, нет точного var */
    props.positive ? "var(--accent-neutral)" : props.negative ? "var(--accent-error)" : "var(--text-primary)"};
`;

const SectionTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: var(--text-primary);
  font-weight: 600;
`;

const HistorySection = styled.div``;

const HistoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
`;

const HistoryRow = styled.tr`
  border-bottom: 1px solid var(--border-light);
`;

const HistoryCell = styled.td`
  padding: 1rem 0.5rem;
  font-size: 0.9rem;
  vertical-align: top;
  color: var(--text-primary);

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    padding-right: 0;
  }
`;

const HistoryOperation = styled.div`
  color: var(--accent-primary);
  margin-bottom: 0.25rem;
`;

const HistoryUser = styled.div`
  color: var(--text-secondary);
  font-size: 0.8rem;
`;

const HistoryTime = styled.div`
  color: var(--text-secondary);
  text-align: center;
`;

const HistoryAmount = styled.div<{ positive?: boolean; negative?: boolean }>`
  font-weight: 500;
  color: ${(props) =>
    /* TODO: positive "var(--accent-neutral)" — дизайнерский серый из --gradient-withdraw, нет точного var */
    props.positive ? "var(--accent-neutral)" : props.negative ? "var(--accent-error)" : "var(--text-primary)"};
`;

const HistoryBalance = styled.div`
  text-align: right;
  font-family: monospace;
`;

const AttachmentsSection = styled.div`
  margin-bottom: 2rem;
`;

const AttachmentTabs = styled.div`
  display: flex;
  border-bottom: 1px solid var(--border-light);
  margin-bottom: 1rem;
`;

const AttachmentTab = styled.button<{ active?: boolean }>`
  background: none;
  border: none;
  color: ${(props) => (props.active ? "var(--accent-primary)" : "var(--text-secondary)")};
  padding: 1rem 2rem;
  cursor: pointer;
  border-bottom: 2px solid
    ${(props) => (props.active ? "var(--accent-primary)" : "transparent")};
  font-size: 1rem;

  &:hover {
    color: var(--text-primary);
  }
`;

const AttachmentContent = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
`;

const AttachmentIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const UploadButton = styled.button`
  background: var(--accent-primary);
  color: black;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  margin-top: 1rem;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: var(--accent-primary-hover);
  }

  &:disabled {
    background: var(--text-muted);
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

function OrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"trader" | "buyer">("trader");
  const [isUploading, setIsUploading] = useState(false);

  const { data: order, isLoading, error, refetch } = useGetOrderByIdQuery(orderId || "");
  const [uploadReceipt] = useUploadOrderReceiptMutation();
  const [updateOrderStatus, { isLoading: isUpdating }] = useUpdateOrderStatusMutation();

  const handleUploadTraderReceipt = () => {
    // Создаем скрытый input для выбора файла
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*,.pdf";
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file && orderId) {
        setIsUploading(true);
        try {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("type", "trader"); // Указываем тип чека

          await uploadReceipt({ orderId, formData }).unwrap();
          alert(`Чек трейдера "${file.name}" успешно загружен!`);
        } catch (error) {
          console.error("Ошибка загрузки файла:", error);
          alert("Произошла ошибка при загрузке файла. Попробуйте еще раз.");
        } finally {
          setIsUploading(false);
        }
      }
    };
    input.click();
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "PENDING":
        return "Ожидает";
      case "PROCESSING":
        return "Обработка";
      case "COMPLETED":
        return "Завершен";
      case "CANCELLED":
        return "Отменен";
      case "FAILED":
        return "Ошибка";
      case "EXPIRED":
        return "Истек";
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <Container>
        <div style={{ padding: "2rem", textAlign: "center" }}>Загрузка...</div>
      </Container>
    );
  }

  if (error || !order) {
    return (
      <Container>
        <div style={{ padding: "2rem", textAlign: "center" }}>
          Ордер не найден
        </div>
      </Container>
    );
  }

  // Сначала создаем базовые данные
  const baseOrderData = {
    id: order.id || orderId || "N/A",
    status: order.status || "unknown",
    operation: "Пополнение",
    createdAt: order.createdAt
      ? new Date(order.createdAt).toLocaleString("ru-RU")
      : "—",
    lastUpdate: order.updatedAt
      ? new Date(order.updatedAt).toLocaleString("ru-RU")
      : "—",
    debitAmount: order.usdtAmount || 0,
    requestedAmount: order.localAmount || order.amount || 0,
    creditAmount: order.localAmount || order.amount || 0,
    currency: "RUB",
    exchangeRate: order.exchangeRate || 0,
  };

  // Парсим метаданные для получения информации о чеках
  let receipts: any = {};
  try {
    const metadata =
      typeof order.metadata === "string"
        ? JSON.parse(order.metadata)
        : order.metadata;
    receipts = metadata?.receipts || {};
  } catch (e) {
    console.warn("Не удалось распарсить metadata:", e);
  }

  // Затем создаем историю на основе базовых данных
  const history = [];
  const frozenAmount = baseOrderData.debitAmount; // Сумма в USDT

  // 1. Заморозка баланса при создании ордера
  history.push({
    operation: "Пополнение - Заморозка баланса при старте ордера",
    user: "Система",
    time: baseOrderData.createdAt,
    amount: -frozenAmount.toFixed(2),
    balance: `Заморожено ${frozenAmount.toFixed(2)} USDT`,
    negative: true,
  });

  // 2. Если ордер завершен успешно - показываем доход провайдера
  if (order.status === "COMPLETED" && order.merchantFee && order.providerFee) {
    const providerIncome = order.providerFee; // Доход провайдера из комиссии мерчанта
    const deductedAmount = frozenAmount - providerIncome; // Списанная сумма

    history.push({
      operation: "Пополнение - Списание замороженного баланса",
      user: "Система",
      time: baseOrderData.lastUpdate,
      amount: -deductedAmount.toFixed(2),
      balance: `Списано ${deductedAmount.toFixed(2)} USDT`,
      negative: true,
    });

    history.push({
      operation: "Пополнение - Начисление комиссии провайдера",
      user: "Система",
      time: baseOrderData.lastUpdate,
      amount: providerIncome.toFixed(2),
      balance: `Получено ${providerIncome.toFixed(2)} USDT`,
      positive: true,
    });
  }

  // 3. Если ордер отменен - разморозка
  if (["CANCELLED", "FAILED", "EXPIRED"].includes(order.status)) {
    history.push({
      operation: "Пополнение - Разморозка баланса при отмене",
      user: "Система",
      time: baseOrderData.lastUpdate,
      amount: frozenAmount.toFixed(2),
      balance: `Разморожено ${frozenAmount.toFixed(2)} USDT`,
      positive: true,
    });
  }

  // Финальный объект с историей
  const orderData = {
    ...baseOrderData,
    history,
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate(-1)}>← Назад</BackButton>
      </Header>

      <MainContent>
        <LeftPanel>
          <DealHeader>
            <DealTitle>Сделка</DealTitle>
            <DealId>ID {orderData.id}</DealId>
          </DealHeader>

          <StatusSection>
            <StatusItem>
              <StatusLabel>Статус</StatusLabel>
              <StatusValue>
                <StatusBadge status={orderData.status}>
                  {getStatusText(orderData.status)}
                </StatusBadge>
              </StatusValue>
            </StatusItem>
            <StatusItem>
              <StatusLabel>Операция</StatusLabel>
              <StatusValue style={{ color: "var(--accent-primary)" }}>
                {orderData.operation}
              </StatusValue>
            </StatusItem>
            <StatusItem>
              <StatusLabel>Дата создания</StatusLabel>
              <StatusValue>{orderData.createdAt}</StatusValue>
            </StatusItem>
          </StatusSection>

          <StatusSection>
            <StatusItem>
              <StatusLabel>Последнее изм.</StatusLabel>
              <StatusValue>{orderData.lastUpdate}</StatusValue>
            </StatusItem>
          </StatusSection>

          <AmountSection>
            <AmountItem>
              <AmountLabel>Сумма списания</AmountLabel>
              <AmountDescription>
                Списано с баланса провайдера
              </AmountDescription>
              <AmountValue negative>
                - {orderData.debitAmount.toFixed(2)} $
              </AmountValue>
            </AmountItem>
            <AmountItem>
              <AmountLabel>Заявленная сумма</AmountLabel>
              <AmountDescription>Сумма, указанная клиентом</AmountDescription>
              <AmountValue positive>
                + {orderData.requestedAmount} {orderData.currency}
              </AmountValue>
            </AmountItem>
            <AmountItem>
              <AmountLabel>Сумма зачисления</AmountLabel>
              <AmountDescription>
                Фактически зачислено провайдеру
              </AmountDescription>
              <AmountValue positive>
                + {orderData.creditAmount} {orderData.currency}
              </AmountValue>
            </AmountItem>
            <AmountItem>
              <AmountLabel>Курс обмена</AmountLabel>
              <AmountValue>
                {orderData.exchangeRate
                  ? `${orderData.exchangeRate.toFixed(2)} ₽/USDT`
                  : "—"}
              </AmountValue>
            </AmountItem>
          </AmountSection>

          <HistorySection>
            <SectionTitle>История изменений сделки</SectionTitle>
            <HistoryTable>
              <thead>
                <HistoryRow>
                  <HistoryCell>
                    <strong>Операция</strong>
                  </HistoryCell>
                  <HistoryCell>
                    <strong>Пользователь</strong>
                  </HistoryCell>
                  <HistoryCell>
                    <strong>Время</strong>
                  </HistoryCell>
                  <HistoryCell>
                    <strong>Сумма</strong>
                  </HistoryCell>
                  <HistoryCell>
                    <strong>Баланс до → после</strong>
                  </HistoryCell>
                </HistoryRow>
              </thead>
              <tbody>
                {orderData.history.map((item, index) => (
                  <HistoryRow key={index}>
                    <HistoryCell>
                      <HistoryOperation>{item.operation}</HistoryOperation>
                    </HistoryCell>
                    <HistoryCell>
                      <HistoryUser>{item.user}</HistoryUser>
                    </HistoryCell>
                    <HistoryCell>
                      <HistoryTime>{item.time}</HistoryTime>
                    </HistoryCell>
                    <HistoryCell>
                      <HistoryAmount
                        positive={!!item.positive}
                        negative={!!item.negative}
                      >
                        {item.amount !== 0 && (item.positive ? "+" : "")}
                        {item.amount !== 0 ? `${item.amount} $` : "0.0000 $"}
                      </HistoryAmount>
                    </HistoryCell>
                    <HistoryCell>
                      <HistoryBalance>{item.balance}</HistoryBalance>
                      {item.negative && (
                        <div style={{ color: "var(--accent-error)", fontSize: "0.8rem" }}>
                          {item.negative}
                        </div>
                      )}
                    </HistoryCell>
                  </HistoryRow>
                ))}
              </tbody>
            </HistoryTable>
          </HistorySection>

          {/* Кнопки управления ордером */}
          {order.status === "PENDING" && (
            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid var(--border-light)' }}>
              <button
                disabled={isUpdating}
                onClick={async () => {
                  if (!window.confirm(`Подтвердить оплату по ордеру?\n\nСтатус будет изменён на COMPLETED.\nБалансы и комиссии будут пересчитаны.`)) return;
                  try {
                    await updateOrderStatus({ id: order.id, status: 'COMPLETED' }).unwrap();
                    alert('Ордер подтверждён! Балансы обновлены.');
                    refetch();
                  } catch (err: any) {
                    alert(`Ошибка: ${err?.data?.message || err?.message || 'Неизвестная ошибка'}`);
                  }
                }}
                style={{
                  flex: 1,
                  padding: '1rem 2rem',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'var(--accent-success)',
                  color: '#ffffff',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: isUpdating ? 'not-allowed' : 'pointer',
                  opacity: isUpdating ? 0.6 : 1,
                  transition: 'all 0.2s',
                }}
              >
                {isUpdating ? 'Обработка...' : '✓ Подтвердить оплату'}
              </button>
              <button
                disabled={isUpdating}
                onClick={async () => {
                  if (!window.confirm(`Отклонить ордер?\nСтатус будет изменён на CANCELLED.`)) return;
                  try {
                    await updateOrderStatus({ id: order.id, status: 'CANCELLED' }).unwrap();
                    alert('Ордер отклонён.');
                    refetch();
                  } catch (err: any) {
                    alert(`Ошибка: ${err?.data?.message || err?.message || 'Неизвестная ошибка'}`);
                  }
                }}
                style={{
                  padding: '1rem 2rem',
                  borderRadius: '8px',
                  border: '1px solid var(--accent-error)',
                  background: 'transparent',
                  color: 'var(--accent-error)',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: isUpdating ? 'not-allowed' : 'pointer',
                  opacity: isUpdating ? 0.6 : 1,
                  transition: 'all 0.2s',
                }}
              >
                ✕ Отклонить
              </button>
            </div>
          )}
          {order.status === "COMPLETED" && (
            <div style={{ marginTop: '2rem', padding: '1rem', background: 'var(--alert-success-bg)', borderRadius: '8px', border: '1px solid var(--alert-success-border)', textAlign: 'center' }}>
              <span style={{ color: 'var(--alert-success-text)', fontWeight: 600, fontSize: '1rem' }}>✓ Ордер завершён</span>
            </div>
          )}
          {["CANCELLED", "FAILED", "EXPIRED"].includes(order.status) && (
            <div style={{ marginTop: '2rem', padding: '1rem', background: 'var(--alert-error-bg)', borderRadius: '8px', border: '1px solid var(--alert-error-border)', textAlign: 'center' }}>
              <span style={{ color: 'var(--alert-error-text)', fontWeight: 600, fontSize: '1rem' }}>✕ Ордер {order.status === "CANCELLED" ? "отменён" : order.status === "EXPIRED" ? "истёк" : "ошибка"}</span>
            </div>
          )}
        </LeftPanel>
      </MainContent>
    </Container>
  );
}

export default OrderDetail;
